import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Calendar, MapPin, ExternalLink, ArrowLeft, Ticket,
  Users, Facebook, ShieldCheck, ChevronRight, BadgeCheck, Eye,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventDetailsPage = ({ user, onLogout, refreshUserData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === "fr";

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Babillard (listings) related to this event
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);

  // Facebook groups related
  const [fbGroups, setFbGroups] = useState({ matched: [], all: [] });
  const [fbLoading, setFbLoading] = useState(true);
  const [showAllGroups, setShowAllGroups] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/events/${id}`);
        setEvent(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "not_found" : "error");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  // Fetch related listings + Facebook groups once we know the event name
  useEffect(() => {
    if (!event?.name) return;
    const eventName = event.name;

    (async () => {
      try {
        setListingsLoading(true);
        const res = await axios.get(
          `${API}/public/billets?event_search=${encodeURIComponent(eventName)}&limit=50`
        );
        setListings(res.data?.listings || []);
      } catch (e) {
        setListings([]);
      } finally {
        setListingsLoading(false);
      }
    })();

    (async () => {
      try {
        setFbLoading(true);
        const res = await axios.get(
          `${API}/facebook-groups/by-event?event_name=${encodeURIComponent(eventName)}`
        );
        setFbGroups({
          matched: res.data?.matched || [],
          all: res.data?.all || [],
        });
      } catch (e) {
        setFbGroups({ matched: [], all: [] });
      } finally {
        setFbLoading(false);
      }
    })();
  }, [event?.name]);

  const scrollTo = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="flex items-center justify-center pt-40">
          <Loader2 className="h-10 w-10 animate-spin text-brand-700" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="flex-1 flex items-center justify-center px-4 pt-20">
          <div className="text-center max-w-md">
            <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isFr ? "Événement introuvable" : "Event not found"}
            </h1>
            <p className="text-gray-600 mb-6">
              {isFr
                ? "Cet événement n'existe plus ou a été retiré."
                : "This event no longer exists or has been removed."}
            </p>
            <button
              onClick={() => navigate("/events")}
              className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-6 py-3 rounded-lg"
              data-testid="event-not-found-back-btn"
            >
              {isFr ? "Voir tous les événements" : "See all events"}
            </button>
          </div>
        </div>
        <Footer variant="simple" />
      </div>
    );
  }

  const hero = event.image || event.gallery?.[0];

  const facebookGroupsToShow = showAllGroups
    ? fbGroups.all
    : (fbGroups.matched.length > 0 ? fbGroups.matched : fbGroups.all.slice(0, 6));

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      <div className="h-16 lg:h-[68px]" />

      {/* HERO */}
      <section
        className="relative text-white overflow-hidden"
        data-testid="event-details-hero"
      >
        <div className="absolute inset-0">
          {hero ? (
            <img
              src={hero}
              alt={event.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-700" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-gray-900/75 to-gray-900/55" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-200 hover:text-white mb-6 transition-colors"
            data-testid="event-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            {isFr ? "Retour" : "Back"}
          </button>

          {event.segment && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-300/15 border border-brand-300/30 rounded-full mb-4">
              <span className="text-xs font-semibold text-brand-200 uppercase tracking-wide">
                {event.segment}
              </span>
            </div>
          )}

          <h1
            className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 max-w-4xl"
            data-testid="event-title"
          >
            {event.name}
          </h1>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-200 text-sm md:text-base mb-8">
            {event.date && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-brand-300" />
                <span>
                  {event.date}
                  {event.time ? ` • ${event.time}` : ""}
                </span>
              </div>
            )}
            {(event.venue || event.city) && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-brand-300" />
                <span>
                  {event.venue && event.city
                    ? `${event.venue}, ${event.city}`
                    : event.venue || event.city}
                </span>
              </div>
            )}
            {event.price_range && (
              <div className="flex items-center gap-1.5">
                <Ticket className="h-4 w-4 text-brand-300" />
                <span>{event.price_range}</span>
              </div>
            )}
          </div>

          {/* TWO MAIN CTAS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <button
              onClick={() => scrollTo("babillard")}
              className="group inline-flex items-center justify-between gap-2 bg-brand-300 hover:bg-brand-400 text-gray-900 font-bold px-5 py-4 rounded-xl transition-all shadow-lg"
              data-testid="cta-babillard"
            >
              <div className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                <span>{isFr ? "Voir le babillard" : "View tickets board"}</span>
              </div>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => scrollTo("fb-groups")}
              className="group inline-flex items-center justify-between gap-2 bg-[#1877F2] hover:bg-[#166fe0] text-white font-bold px-5 py-4 rounded-xl transition-all shadow-lg"
              data-testid="cta-fb-groups"
            >
              <div className="flex items-center gap-2">
                <Facebook className="h-5 w-5" />
                <span>{isFr ? "Groupes Facebook" : "Facebook groups"}</span>
              </div>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          {event.url && (
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-white mt-5 transition-colors"
              data-testid="event-ticketmaster-link"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {isFr ? "Voir sur Ticketmaster" : "View on Ticketmaster"}
            </a>
          )}
        </div>
      </section>

      {/* INFO SECTION */}
      {(event.info || event.seatmap) && (
        <section className="py-10 border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
            {event.info && (
              <div className="lg:col-span-2">
                <h2 className="text-xl font-bold text-gray-900 mb-3">
                  {isFr ? "À propos de l'événement" : "About this event"}
                </h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {event.info}
                </p>
              </div>
            )}
            {event.seatmap && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">
                  {isFr ? "Plan de la salle" : "Seat map"}
                </h2>
                <a href={event.seatmap} target="_blank" rel="noopener noreferrer">
                  <img
                    src={event.seatmap}
                    alt="Seat map"
                    className="w-full rounded-lg border border-gray-200 hover:opacity-90 transition"
                  />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* BABILLARD (Listings) */}
      <section
        id="babillard"
        className="py-12 md:py-16 bg-gray-50"
        data-testid="event-babillard-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Ticket className="h-5 w-5 text-brand-700" />
                <span className="text-xs font-bold text-brand-700 uppercase tracking-wider">
                  {isFr ? "Babillard" : "Tickets Board"}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                {isFr ? "Billets en vente par nos membres" : "Tickets for sale by our members"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isFr
                  ? "Transactions sécurisées via Middleman — aucun échange direct."
                  : "Secure transactions through Middleman — no direct exchange."}
              </p>
            </div>
            {listings.length > 0 && (
              <span className="text-sm font-semibold text-gray-700" data-testid="babillard-count">
                {listings.length} {isFr ? "annonce(s)" : "listing(s)"}
              </span>
            )}
          </div>

          {listingsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : listings.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-8 md:p-12 text-center">
              <Ticket className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {isFr
                  ? "Aucune annonce active pour cet événement"
                  : "No active listings for this event yet"}
              </h3>
              <p className="text-gray-600 mb-5 max-w-md mx-auto">
                {isFr
                  ? "Soyez le premier à publier vos billets sur Middleman et atteignez les acheteurs sérieux."
                  : "Be the first to list your tickets on Middleman and reach serious buyers."}
              </p>
              <button
                onClick={() => navigate(user ? "/dashboard?openListing=1" : "/auth")}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-6 py-3 rounded-lg transition-all"
                data-testid="empty-babillard-cta"
              >
                {isFr ? "Vendre mes billets" : "Sell my tickets"}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/billet/${l.slug}`)}
                  className="group text-left bg-white border border-gray-200 hover:border-brand-300 hover:shadow-lg rounded-xl p-5 transition-all"
                  data-testid={`listing-card-${l.slug}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">{l.event_date}</p>
                      <p className="font-bold text-gray-900 line-clamp-1 group-hover:text-brand-700">
                        {l.event_name}
                      </p>
                    </div>
                    <BadgeCheck className="h-5 w-5 text-brand-600 flex-shrink-0 ml-2" />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-4">
                    {l.section && (
                      <div>
                        <p className="text-gray-500">{isFr ? "Section" : "Section"}</p>
                        <p className="font-semibold text-gray-900 truncate">{l.section}</p>
                      </div>
                    )}
                    {l.row && (
                      <div>
                        <p className="text-gray-500">{isFr ? "Rangée" : "Row"}</p>
                        <p className="font-semibold text-gray-900 truncate">{l.row}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500">{isFr ? "Quantité" : "Qty"}</p>
                      <p className="font-semibold text-gray-900">{l.ticket_count}</p>
                    </div>
                  </div>
                  <div className="flex items-end justify-between pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500">{isFr ? "Prix / billet" : "Price / ticket"}</p>
                      <p className="text-xl font-extrabold text-gray-900">
                        {l.price_per_ticket} {l.currency}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 group-hover:text-brand-800">
                      {isFr ? "Voir" : "View"}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                  {typeof l.views === "number" && (
                    <p className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                      <Eye className="h-3 w-3" /> {l.views}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Trust strip */}
          <div className="mt-8 bg-brand-50/60 border border-brand-200 rounded-xl p-4 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-brand-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 leading-relaxed">
              {isFr
                ? "Middleman vérifie chaque transaction. Les billets ne sont transférés qu'après confirmation du paiement. Aucun échange direct entre acheteur et vendeur."
                : "Middleman verifies every transaction. Tickets are transferred only after payment is confirmed. No direct exchange between buyer and seller."}
            </p>
          </div>
        </div>
      </section>

      {/* FACEBOOK GROUPS */}
      <section
        id="fb-groups"
        className="py-12 md:py-16"
        data-testid="event-fb-groups-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Facebook className="h-5 w-5 text-[#1877F2]" />
                <span className="text-xs font-bold text-[#1877F2] uppercase tracking-wider">
                  {isFr ? "Communauté" : "Community"}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                {isFr ? "Groupes Facebook recommandés" : "Recommended Facebook groups"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {fbGroups.matched.length > 0
                  ? (isFr
                      ? `${fbGroups.matched.length} groupe(s) pertinent(s) pour cet événement.`
                      : `${fbGroups.matched.length} group(s) relevant to this event.`)
                  : (isFr
                      ? "Aucune correspondance directe — voici nos groupes vérifiés."
                      : "No direct match — here are our verified groups.")}
              </p>
            </div>
          </div>

          {fbLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : facebookGroupsToShow.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 text-center">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">
                {isFr ? "Aucun groupe Facebook disponible pour le moment." : "No Facebook groups available yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {facebookGroupsToShow.map((g) => (
                  <a
                    key={g.id}
                    href={g.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white border border-gray-200 hover:border-[#1877F2]/40 hover:shadow-lg rounded-xl overflow-hidden transition-all"
                    data-testid={`fb-group-card-${g.id}`}
                  >
                    {g.image_url && (
                      <div className="h-32 w-full overflow-hidden bg-gray-100">
                        <img
                          src={
                            g.image_url.startsWith("/")
                              ? `${process.env.REACT_APP_BACKEND_URL}${g.image_url}`
                              : g.image_url
                          }
                          alt={g.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start gap-2.5">
                        <div className="bg-[#1877F2]/10 p-2 rounded-lg">
                          <Facebook className="h-4 w-4 text-[#1877F2]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 line-clamp-1 group-hover:text-[#1877F2]">
                            {g.name}
                          </p>
                          {g.member_count && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {g.member_count} {isFr ? "membres" : "members"}
                            </p>
                          )}
                        </div>
                      </div>
                      {g.description && (
                        <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                          {g.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400">
                          {fbGroups.matched.find((m) => m.id === g.id)
                            ? (isFr ? "Pertinent" : "Relevant")
                            : (isFr ? "Communauté" : "Community")}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1877F2]">
                          {isFr ? "Rejoindre" : "Join"}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              {fbGroups.matched.length > 0 && !showAllGroups && fbGroups.all.length > fbGroups.matched.length && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllGroups(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-800"
                    data-testid="show-all-groups-btn"
                  >
                    {isFr
                      ? `Voir tous les groupes (${fbGroups.all.length})`
                      : `See all groups (${fbGroups.all.length})`}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Submit your own group */}
          <div className="mt-8 bg-gradient-to-r from-brand-50 to-brand-100 border border-brand-200 rounded-xl p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-semibold text-gray-900">
                {isFr ? "Vous gérez un groupe Facebook ?" : "Do you manage a Facebook group?"}
              </p>
              <p className="text-sm text-gray-600">
                {isFr
                  ? "Soumettez-le pour apparaître ici."
                  : "Submit it to be featured here."}
              </p>
            </div>
            <button
              onClick={() => navigate("/submit-group")}
              className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-lg text-sm"
              data-testid="submit-group-cta"
            >
              {isFr ? "Soumettre un groupe" : "Submit a group"}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default EventDetailsPage;
